import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/context/AuthContext';
import { TourProvider, useTour } from '../../shared/lib/TourContext';
import {
  shouldStartDashboardTourForUser,
  completeDashboardTourForUser,
  resetDashboardTourForUser,
} from '../../shared/lib/dashboardTourState';
import logo from '../../shared/assets/logos/ARBCOR_Text_only.png';
import Sidebar from './components/Sidebar';
import OverviewView from './components/OverviewView';
import OverviewOldView from './components/OverviewOldView';
import IntegrationsView from '../integrations/components/IntegrationsView';
import DataView from './components/DataView';
import CalendarView from './components/CalendarView';
import TeamView from './components/TeamView';
import InboxView from './components/InboxView';
import SettingsView from './components/SettingsView';
import VendorEcosystemView from './components/VendorEcosystemView';
import AdminView from './components/AdminView';
import ChartOfAccountsView from './components/ChartOfAccountsView';
import BankingView from './components/BankingView';
import InvoicingView from './components/InvoicingView';
import CustomersView from './components/CustomersView';
import EnrichmentComparisonView from './components/EnrichmentComparisonView';
import TaxonomyView from './components/TaxonomyView';
import SpendAnalyticsView from './components/SpendAnalyticsView';
import SpendAnalyticsProView from './components/SpendAnalyticsProView';
import SpendAnalyticsV2View from './components/SpendAnalyticsV2View';
import AntirView from './components/AntirView';
import EnrichmentV3View from './components/EnrichmentV3View';
import VendorCatalogView from './components/VendorCatalogView';
import VendorTaxonomyView from './components/VendorTaxonomyView';
import BillsView from './components/BillsView';
import PurchaseOrdersView from './components/PurchaseOrdersView';
import DocumentDetailView from './components/DocumentDetailView';
import VendorsView from './components/VendorsView';
import VendorProductsView from './components/VendorProductsView';
import PaymentCalendar from './components/PaymentCalendar';
import CalendarPro from './components/CalendarPro';
import ProductTour from './components/ProductTour';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const Dashboard = () => {
  const [loaded, setLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('arbcor_theme') || 'v2');
  const { profile, user, signOut, companies, activeCompany } = useAuth();
  const { start: startTour, stop: stopTour, isOpen: showProductTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive activeView from the URL path
  const pathSegment = location.pathname.replace('/dashboard', '').replace(/^\//, '') || 'overview';
  const activeView = pathSegment.split('/')[0] || 'overview';

  const handleViewChange = (viewId, params) => {
    const path = viewId === 'overview' ? '/dashboard' : `/dashboard/${viewId}`;
    if (params?.filter) {
      navigate(`${path}?filter=${params.filter}`);
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const updateTime = () => setCurrentTime(
      new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // Auto-start tour for new users
  useEffect(() => {
    if (showProductTour) return;
    if (companies.length === 0) return;
    if (!shouldStartDashboardTourForUser(user)) return;

    if (activeView !== 'overview') {
      navigate('/dashboard', { replace: true });
      return;
    }

    const timer = setTimeout(() => startTour(), 250);
    return () => clearTimeout(timer);
  }, [activeView, companies.length, navigate, showProductTour, user, startTour]);

  const handleTourClose = useCallback(() => {
    completeDashboardTourForUser(user);
    stopTour();
  }, [user, stopTour]);

  const handleRestartTour = useCallback(() => {
    resetDashboardTourForUser(user);
    setUserMenuOpen(false);
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
    setTimeout(() => startTour(), 50);
  }, [user, location.pathname, navigate, startTour]);

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const displayName = profile?.full_name || user?.name || 'User';
  const initials = getInitials(profile?.full_name || user?.name || user?.email || '');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const userMenuRef = useRef(null);
  const userBtnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target) && !userBtnRef.current?.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpenMenu = () => {
    if (userBtnRef.current) {
      const rect = userBtnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setUserMenuOpen((o) => !o);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-theme={theme}>
      <Sidebar loaded={loaded} user={{ full_name: displayName, email: user?.email, avatar_url: profile?.avatar_url }} onLogout={handleLogout} activeView={activeView} onViewChange={handleViewChange} company={activeCompany} collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />

      {/* Main content */}
      <div className={`flex-1 min-w-0 flex flex-col min-h-screen transition-[margin] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Top bar */}
        <header
          className="dashboard-header h-14 bg-white border-b border-zinc-100 px-4 sm:px-6 flex items-center justify-between shrink-0 overflow-visible transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(-8px)',
            transitionDelay: '200ms',
          }}
        >
          <div className="flex items-center gap-3 flex-1 max-w-md">
            {/* Mobile logo */}
            <Link to="/" className="lg:hidden shrink-0">
              <img src={logo} alt="Arbcor" className="h-4 w-auto" draggable="false" />
            </Link>

            {/* Search */}
            <div className="flex-1 relative hidden sm:block">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="7" cy="7" r="5" />
                <path d="M11 11L14 14" />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-9 pr-4 py-2 bg-zinc-50 border-0 text-sm text-zinc-900 placeholder-zinc-400 rounded-md outline-none focus:ring-1 focus:ring-zinc-900 transition-shadow duration-300"
              />
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Theme toggle */}
            <button
              onClick={() => {
                const next = theme === 'v2' ? 'v2-dark' : 'v2';
                setTheme(next);
                localStorage.setItem('arbcor_theme', next);
              }}
              title={theme === 'v2' ? 'Switch to dark mode' : 'Switch to light mode'}
              className="relative w-9 h-9 flex items-center justify-center hover:bg-zinc-50 rounded-md transition-colors"
            >
              {theme === 'v2' ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zinc-500">
                  <circle cx="8" cy="8" r="3.5" />
                  <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.7 3.7l1.4 1.4M10.9 10.9l1.4 1.4M3.7 12.3l1.4-1.4M10.9 5.1l1.4-1.4" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zinc-500">
                  <path d="M13.5 8.5a5.5 5.5 0 1 1-6-6 4.5 4.5 0 0 0 6 6z" />
                </svg>
              )}
            </button>

            {/* Notifications */}
            <button className="relative w-9 h-9 flex items-center justify-center hover:bg-zinc-50 rounded-md transition-colors">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="text-zinc-500"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5" />
                <path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
              </svg>
              <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-red-500 rounded-full" />
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                ref={userBtnRef}
                onClick={handleOpenMenu}
                className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-zinc-50 rounded-md transition-colors"
              >
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-zinc-900 flex items-center justify-center text-[10px] text-white font-medium shrink-0">
                    {initials}
                  </div>
                )}
                <div className="hidden md:flex flex-col items-start leading-tight">
                  <span className="text-sm text-zinc-700 font-medium">{displayName}</span>
                  <span className="text-[11px] text-zinc-400">{user?.email}</span>
                </div>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                  className={`text-zinc-400 hidden md:block transition-transform duration-150 ${userMenuOpen ? 'rotate-180' : ''}`}>
                  <path d="M2 4l4 4 4-4" />
                </svg>
              </button>

              {userMenuOpen && createPortal(
                <div
                  ref={userMenuRef}
                  className="fixed w-56 bg-white border border-zinc-200 rounded-xl shadow-xl shadow-zinc-900/10 z-[9999] overflow-hidden"
                  style={{ top: menuPos.top, right: menuPos.right }}
                >
                  <div className="px-4 py-3 border-b border-zinc-100">
                    <p className="text-sm font-medium text-zinc-900">{displayName}</p>
                    <p className="text-[11px] text-zinc-400 truncate">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    <button onClick={() => { handleViewChange('settings'); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zinc-400">
                        <circle cx="8" cy="8" r="2.5" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M13 3l-1.5 1.5M4.5 11.5L3 13" />
                      </svg>
                      Settings
                    </button>
                    <button onClick={() => { handleViewChange('team'); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400">
                        <circle cx="6" cy="5" r="2.5" /><path d="M1 13c0-2.5 2.2-4 5-4s5 1.5 5 4" />
                        <path d="M11 3a2.5 2.5 0 0 1 0 5M15 13c0-2-1.5-3.5-4-3.8" />
                      </svg>
                      Team
                    </button>
                  </div>
                  <div className="py-1 border-t border-zinc-100">
                    <button onClick={handleRestartTour}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors text-left">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400">
                        <path d="M2.5 8A5.5 5.5 0 1 0 4 4.1" />
                        <path d="M2 2v4h4" />
                      </svg>
                      Product Tour
                    </button>
                    <div className="flex items-center gap-1 px-4 py-2">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium mr-auto">Theme</span>
                      {[
                        { id: 'v2', label: 'Light', icon: (
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="5" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M3.5 12.5l1.4-1.4M11.1 4.9l1.4-1.4" /></svg>
                        )},
                        { id: 'v2-dark', label: 'Dark', icon: (
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13.5 8.5a5.5 5.5 0 1 1-6-6 4.5 4.5 0 0 0 6 6z" /></svg>
                        )},
                      ].map(t => (
                        <button
                          key={t.id}
                          onClick={() => { setTheme(t.id); localStorage.setItem('arbcor_theme', t.id); }}
                          title={t.label}
                          className={`p-1.5 rounded-md transition-colors ${theme === t.id ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                        >
                          {t.icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="py-1 border-t border-zinc-100">
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors text-left">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-400">
                        <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3M11 11l3-3-3-3M14 8H6" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main key={activeCompany?.companyId} className="dashboard-content flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-6 pb-24 lg:pb-6">
          {activeView === 'admin' ? (
            <AdminView loaded={loaded} />
          ) : companies.length === 0 ? (
            /* Empty state — no company yet */
            <div className="flex-1 flex items-center justify-center min-h-[60vh]">
              <div
                className="text-center max-w-md transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(16px)',
                  transitionDelay: '300ms',
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'scale(1)' : 'scale(0.8)',
                    transitionDelay: '400ms',
                  }}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 28 28"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="text-zinc-400"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 24V8l8-4 8 4v16" />
                    <path d="M20 24V12l4-2v14" />
                    <path d="M4 24h20" />
                    <rect x="8" y="12" width="3" height="3" />
                    <rect x="14" y="12" width="3" height="3" />
                    <rect x="8" y="18" width="3" height="3" />
                    <rect x="14" y="18" width="3" height="3" />
                  </svg>
                </div>

                <h2
                  className="text-2xl font-light text-zinc-900 mb-2 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transitionDelay: '500ms',
                  }}
                >
                  Ready to wake up your data?
                </h2>
                <p
                  className="text-sm text-zinc-400 mb-8 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transitionDelay: '600ms',
                  }}
                >
                  Create a company to start syncing your tools, or join one with an invite link.
                </p>

                <div
                  className="flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transitionDelay: '700ms',
                  }}
                >
                  <Link
                    to="/onboarding"
                    className="px-6 py-2.5 bg-zinc-900 text-white text-sm tracking-wider uppercase hover:bg-zinc-800 transition-colors duration-300 rounded-md"
                  >
                    Create a Company
                  </Link>
                  <Link
                    to="/onboarding"
                    className="px-6 py-2.5 border border-zinc-200 text-zinc-600 text-sm tracking-wider uppercase hover:border-zinc-900 hover:text-zinc-900 transition-colors duration-300 rounded-md"
                  >
                    Join a Company
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div key={activeView} className="animate-page-in">
            <Routes>
              <Route index element={<OverviewView loaded={loaded} onNavigate={handleViewChange} />} />
              <Route path="overview-old" element={<OverviewOldView loaded={loaded} onNavigate={handleViewChange} />} />
              <Route path="calendar" element={<CalendarView loaded={loaded} onNavigateToIntegrations={() => navigate('/dashboard/integrations')} />} />
              <Route path="payment-calendar" element={<PaymentCalendar loaded={loaded} onNavigateToIntegrations={() => navigate('/dashboard/integrations')} />} />
              <Route path="calendar-pro" element={<CalendarPro loaded={loaded} onNavigateToIntegrations={() => navigate('/dashboard/integrations')} />} />
              <Route path="team" element={<TeamView loaded={loaded} />} />
              <Route path="documents" element={<InboxView loaded={loaded} />} />
              <Route path="document-detail" element={<DocumentDetailView loaded={loaded} />} />
              <Route path="settings" element={<SettingsView loaded={loaded} />} />
              <Route path="bills" element={<BillsView loaded={loaded} />} />
              <Route path="purchase-orders" element={<PurchaseOrdersView loaded={loaded} />} />
              <Route path="vendors" element={<VendorsView loaded={loaded} />} />
              <Route path="vendor-products" element={<VendorProductsView loaded={loaded} />} />
              <Route path="vendor-network" element={<VendorEcosystemView loaded={loaded} />} />
              {(window.location.hostname === 'dev.arbcor.com' || window.location.hostname === 'localhost') && (
                <>
                  <Route path="integrations" element={<IntegrationsView loaded={loaded} />} />
                  <Route path="data" element={<DataView loaded={loaded} onNavigateToIntegrations={() => navigate('/dashboard/integrations')} />} />
                  <Route path="banking" element={<BankingView loaded={loaded} onNavigateToIntegrations={() => navigate('/dashboard/integrations')} />} />
                  <Route path="customers" element={<CustomersView loaded={loaded} onNavigateToInvoicing={() => navigate('/dashboard/invoicing')} />} />
                  <Route path="invoicing" element={<InvoicingView loaded={loaded} />} />
                  <Route path="enrichment" element={<EnrichmentComparisonView loaded={loaded} />} />
                  <Route path="taxonomies" element={<TaxonomyView loaded={loaded} />} />
                  <Route path="analytics" element={<SpendAnalyticsView loaded={loaded} />} />
                  <Route path="analytics-pro" element={<SpendAnalyticsProView loaded={loaded} />} />
                  <Route path="analytics-v2" element={<SpendAnalyticsV2View loaded={loaded} />} />
                  <Route path="enrichment-v3" element={<EnrichmentV3View loaded={loaded} />} />
                  <Route path="vendor-catalog" element={<VendorCatalogView loaded={loaded} />} />
                  <Route path="vendor-taxonomy" element={<VendorTaxonomyView loaded={loaded} />} />
                  <Route path="antir" element={<AntirView loaded={loaded} />} />
                  <Route path="chart-of-accounts" element={<ChartOfAccountsView loaded={loaded} />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </div>
          )}
        </main>
      </div>

      <ProductTour
        onComplete={handleTourClose}
        onSkip={handleTourClose}
      />
    </div>
  );
};

const DashboardWithTour = () => (
  <TourProvider>
    <Dashboard />
  </TourProvider>
);

export default DashboardWithTour;
